import axios from 'axios';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import type { ArtifactKey, ArtifactType, AuditableType } from 'bc-model';
import type { Edge } from 'bc-artifacts';
import { getBasePath, includeBasePath } from '../utils/url-utils';
import { setupCache } from 'axios-cache-interceptor';
import { io } from 'socket.io-client';
import { getEnvironmentVariable } from './envStore';
import getApiVersion from './apiVersion';
import client from '../client';

type AxiosFn<P extends any[], R> = (...args: P) => Promise<R | null>;
type ApiFnType<F> = F extends AxiosFn<infer P, infer R> ? AxiosFn<P, R> : AxiosFn<any, any>;

type VersionedType = { version: number | string };

type GraphType = {
  edges?: {
    [x: number]: Edge;
  };
};

type SucceedOnReturnType = {
  validateStatus: (status: number) => boolean;
};

const isoDateTimeFormats = [
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z)/,
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z)/,
  /\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z)/,
];

const isoDateTime = new RegExp(`^${isoDateTimeFormats.map((fmt) => fmt.source).join('|')}$`);

function parseISODate(_key, value) {
  if (typeof value === 'string' && isoDateTime.test(value)) {
    return moment.utc(value).toDate();
  }

  return value;
}
const SENDER_ID = uuidv4();
const API_VERSION = getApiVersion();
const updateVerbs = new Set(['post', 'put', 'delete']);
const instance = axios.create({
  baseURL: getBasePath(),
  headers: {
    'X-1B-Sender-ID': SENDER_ID,
    'X-API-Version': API_VERSION,
    // Add test cookie for collab web worker testing
    Cookie: typeof process !== 'undefined' ? process.env.TEST_COOKIE : undefined,
  },
  validateStatus: (status) => status >= 200 && status < 300,
  transformResponse: [
    (data) => {
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data, parseISODate);
        } catch (e) {
          /* Ignore */
        }
      }
      return data;
    },
  ],
});

// Create axios instance with default ttl
const axiosWithCache = setupCache(instance, {
  ttl: 2500, // Default TTL until env is loaded
});

// Load environment variables without blocking
const loadApiClientEnv = async (): Promise<void> => {
  // We can't fetch environment variables in a vitest environment
  // so don't attempt to load them which will fail. Tests should mock appropriate variables.
  if (typeof process !== 'undefined' && process?.env?.VITEST === 'true') return;
  try {
    // Update cache TTL once env is loaded
    const apiCacheTTL = await getEnvironmentVariable('apiCacheTTL');
    if (apiCacheTTL) {
      axiosWithCache.defaults.cache.ttl = apiCacheTTL;
    }
  } catch (e) {
    console.error('Error fetching environment variables', e);
  }
};

const FALLBACK_RETRY_AFTER_MS = 30_000;

axiosWithCache.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 429) {
      const retryAfterSec = Number(error.response.headers?.['retry-after']);
      const defaultRetryAfterMs = client.getDefaultRetryAfterMs?.() ?? FALLBACK_RETRY_AFTER_MS;
      const retryAfterMs = retryAfterSec > 0 ? retryAfterSec * 1000 : defaultRetryAfterMs;
      client.onRateLimited?.(retryAfterMs);
      error.cause = 'RATE_LIMITED';
      throw error;
    }

    if (error.response && error.response.status === 403 && updateVerbs.has(error.config.method)) {
      throw new Error(error.message, { cause: 'ACCESS_DENIED' });
    }

    throw error;
  }
);

const logoutUser = (reason?: string): void => {
  if (window) {
    const next = encodeURIComponent(
      window.location.pathname.replace('logout', '').replace(getBasePath(), '')
    );
    const logoutUrl = includeBasePath(
      `/api/auth/logout?next=${next}${reason ? `&reason=${reason}` : ''}`
    );
    window.location.href = logoutUrl;
  }
};

function succeedOn(statuses: number[]): SucceedOnReturnType {
  return {
    validateStatus: (status: number) =>
      (status >= 200 && status < 300) || statuses.includes(status),
  };
}

const succeedOn404 = succeedOn([404]);

const sync = io(`/sync`, {
  path: includeBasePath(`/socket.io`),
  withCredentials: true,
  autoConnect: false,
  auth: {
    apiVersion: API_VERSION,
  },
});

const serializeArtifactKey = (artifactKey?: ArtifactKey): string => {
  if (!artifactKey?.type || !artifactKey?.id) return '';
  return `${artifactKey.id}:${artifactKey.type}`;
};

const trimPayload = (payload: ArtifactType): Omit<ArtifactType, keyof AuditableType> => {
  const { createdBy, createdAt, updatedBy, updatedAt, version, ...rest } = payload;

  return rest;
};

if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
  (window as any).testApi = axiosWithCache;
}
export {
  succeedOn,
  succeedOn404,
  SENDER_ID,
  sync,
  logoutUser,
  loadApiClientEnv,
  serializeArtifactKey,
  trimPayload,
};
export type { GraphType, ApiFnType, VersionedType };
export default axiosWithCache;
